import { useState, useEffect } from 'react';
  const perms = detectPerms();
import { useNavigate } from 'react-router-dom';
import {
  Search, Loader2, AlertCircle, X, Ticket, Eye, Phone, Mail
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TICKET_STATUSES = ['Open', 'In Progress', 'Ready to Dispatch', 'Cancelled', 'Void'];

const STATUS_STYLES = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  'Ready to Dispatch': 'bg-emerald-100 text-emerald-700',
  'Cancelled': 'bg-red-100 text-red-700',
  'Void': 'bg-slate-200 text-slate-600',
};

export const Tickets = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [error, setError] = useState('');

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/tickets`, getAuthHeaders());
      setItems(res.data);
    } catch (err) { setError('Failed to fetch tickets'); }
    finally { setLoading(false); }
  };

  const filtered = items.filter(i => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      i.ticket_number?.toLowerCase().includes(q) ||
      i.lead_name?.toLowerCase().includes(q) ||
      i.lead_email?.toLowerCase().includes(q) ||
      i.quotation_number?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'All' || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = TICKET_STATUSES.reduce((acc, s) => {
    acc[s] = items.filter(i => i.status === s).length;
    return acc;
  }, {});

  return (
    <div data-testid="tickets-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Tickets</h1>
          <p className="text-slate-600 mt-1">Service tickets auto-created from approved quotations</p>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {TICKET_STATUSES.map(s => (
          <button key={s} onClick={() => setStatusFilter(statusFilter === s ? 'All' : s)}
            data-testid={`ticket-status-filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
            className={`bg-white rounded-sm border p-3 text-left transition-colors ${
              statusFilter === s ? 'border-orange-500 ring-1 ring-orange-500' : 'border-slate-200 hover:border-slate-300'
            }`}>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mb-1 ${STATUS_STYLES[s]}`}>{s}</span>
            <p className="text-xl font-bold text-slate-900">{statusCounts[s] || 0}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input type="text" placeholder="Search by ticket #, lead name, email or quotation #..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} data-testid="ticket-search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none" />
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" /><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-orange-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16" data-testid="tickets-empty-state">
            <Ticket className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No tickets found</h3>
            <p className="text-slate-500">Tickets are auto-created when quotations are approved</p>
          </div>
        ) : (
          <>
            <div className="hidden md:grid grid-cols-9 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Ticket #</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Lead</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Quotation</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Assigned To</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Tasks</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Date</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>
            {filtered.map(item => (
              <div key={item.id} data-testid={`ticket-row-${item.id}`}
                className="grid grid-cols-1 md:grid-cols-9 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center">
                <div className="font-mono font-bold text-sm text-slate-900">{item.ticket_number}</div>
                <div className="col-span-2">
                  <span className="font-medium text-slate-900 block">{item.lead_name || '-'}</span>
                  <span className="text-xs text-slate-500">{item.lead_email || ''}</span>
                </div>
                <div className="text-sm text-slate-600 font-mono">{item.quotation_number || '-'}</div>
                <div className="text-sm">{item.assigned_to_name ? <span className="text-orange-600 font-medium">{item.assigned_to_name}</span> : <span className="text-slate-400">None</span>}</div>
                <div>
                  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${STATUS_STYLES[item.status] || 'bg-slate-100 text-slate-700'}`}>
                    {item.status}
                  </span>
                </div>
                <div className="text-sm text-slate-600">{item.task_count}</div>
                <div className="text-xs text-slate-500">
                  {item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                </div>
                <div>
                  <button onClick={() => navigate(`/admin/services/tickets/${item.id}`)} data-testid={`view-ticket-btn-${item.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="View Details">
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
